import { useCallback, useContext, useMemo } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import useFavurTranslation from 'hooks/useFavurTranslation'
import useHistoryUtils from 'hooks/useHistoryUtils'
import useModule from 'hooks/useModule'
import useRolesViews from 'hooks/useRolesViews'
import { ShareUserDataFormContext } from 'pages/Tasks/ShareUserData/Details'
import usePerformEventToTask from 'pages/Tasks/hooks/usePerformEventToTask'
import type {
  AbsenceRequestTaskT,
  MonthlySheetTaskT,
  ShareUserDataTaskT,
  ShiftUpdateTaskT,
  TaskT,
} from 'pages/Tasks/types'
import { canSendCommentAndReminder } from 'pages/Tasks/utils'
import { useLocation } from 'react-router'
import { useTimelineActionButtonContext } from 'redesign/services/timeline-action-button/use-timeline-action-button-context'
import { useToastContext } from 'redesign/services/toast/use-toast-context'
import { classicRoutes } from 'services/RoutesProvider/routes'
import { TASK_TYPES } from '../../ContentLists/TaskList/constants'
import type { UseActionButtonsProps } from '../../monthly-sheet-details/types'
import { getIsLegacyTimeline } from '../../timeline/factory/get-is-legacy-timeline'
import { resolveActionButtons } from '../utils/resolve-action-buttons'
import { useActionHandler } from './use-action-handler'

export const useActionButtons = ({
  task,
  refetch,
  workflowPermissions,
  prevMonthTask,
  timelineItem,
  setShowAbsenceForm,
  onDelete,
}: UseActionButtonsProps) => {
  const { t } = useFavurTranslation()
  const { openToast } = useToastContext()
  const { breakpoints } = useTheme()
  const { pathname } = useLocation()
  const isAbsencePlanner = useMemo(() => pathname.includes(classicRoutes.absencePlanner), [pathname])
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'))
  const displayTeamPlanButton = useMemo(() => {
    if (isAbsencePlanner) {
      if (!isSmallScreen) return false
    }

    return true
  }, [isAbsencePlanner, isSmallScreen])
  const isLegacy = getIsLegacyTimeline(task)
  const { sendEvent, loading: eventLoading } = usePerformEventToTask(task?.favurUuid ?? '')
  const { setMessageDrawerProps, setDialogProps, setIsBalanceDrawerOpen, setAbsencePlannerDrawerProps } =
    useTimelineActionButtonContext()
  const { isOffice, persons } = useRolesViews()
  const { hasPersonAccessToModule } = useModule()
  const { pushWithRole } = useHistoryUtils()
  const { setShowForm } = useContext(ShareUserDataFormContext)
  const { canSendComment, canSendReminder } = canSendCommentAndReminder(
    isOffice,
    persons,
    task as TaskT<AbsenceRequestTaskT | MonthlySheetTaskT | ShareUserDataTaskT | ShiftUpdateTaskT>,
    workflowPermissions,
    hasPersonAccessToModule,
  )
  const { handleButtonAction } = useActionHandler({
    task,
    prevMonthTask,
    setDialogProps,
    refetch,
    sendEvent,
    eventLoading,
    t,
    onDelete,
    setAbsencePlannerDrawerProps,
    isSmallScreen,
  })

  const openForm = useCallback(() => {
    if (task.taskType === TASK_TYPES.shareUserData) setShowForm(true)
    if (task.taskType === TASK_TYPES.absenceRequest && setShowAbsenceForm) setShowAbsenceForm(true)
  }, [setShowAbsenceForm, setShowForm, task.taskType])

  const { mainActions, hiddenActions } = resolveActionButtons({
    task,
    refetch,
    t,
    sendEvent,
    setMessageDrawerProps,
    setDialogProps,
    handleButtonAction,
    isOffice,
    canSendComment: canSendComment && !isLegacy,
    canSendReminder: canSendReminder && !isLegacy,
    navigate: pushWithRole,
    prevMonthTask,
    openForm,
    timelineItem,
    openBalanceDrawer: () => setIsBalanceDrawerOpen(true),
    displayTeamPlanButton,
    openToast,
  })

  return { mainActions, hiddenActions }
}
